import React, { useState, useEffect, useRef, useContext } from 'react';
import { 
  Container, 
  Box, 
  Typography, 
  TextField, 
  Button, 
  CircularProgress,
  Grid,
  Paper,
  Alert,
  Chip,
  Switch
} from '@mui/material';
import EmailIcon from '@mui/icons-material/Email';
import LockIcon from '@mui/icons-material/Lock';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import DarkModeIcon from '@mui/icons-material/DarkMode';
import { UseApi } from '../Hooks/UseApi';
import { UseAuthRoles } from '../Hooks/UseAuthRoles';
import { ThemeContext } from '../ThemeContext';

const SettingsPage = () => {
  const api = UseApi();
  const { getSubscriptionLevel } = UseAuthRoles();
  const currentTier = getSubscriptionLevel();
  const { darkMode, toggleDarkMode } = useContext(ThemeContext);
  
  const dataLoaded = useRef(false);
  const shopDataLoaded = useRef(false);
  
  const [userData, setUserData] = useState({
    first_name: '',
    last_name: '',
    email: '',
  });
  
  const [shopData, setShopData] = useState({
    name: '',
    phone: '',
    email: '',
    address: '',
    city: '',
    state: '',
    zip_code: ''
  });

  // Deposit settings state
  const [depositSettings, setDepositSettings] = useState({
    default_deposit_amount: ''
  });
  const [depositSaving, setDepositSaving] = useState(false);
  const [depositSuccess, setDepositSuccess] = useState(false);
  const [depositError, setDepositError] = useState(null);
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [shopSaving, setShopSaving] = useState(false);
  const [error, setError] = useState(null);
  const [shopError, setShopError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [shopSuccess, setShopSuccess] = useState(false);

  const isMounted = useRef(true);

  // Check if user has access to aftercare email feature
  const hasAftercareAccess = ['standard', 'growth_studio'].includes(currentTier);

  useEffect(() => {
    if (!dataLoaded.current || !shopDataLoaded.current) {
      fetchData();
    }
    
    return () => {
      isMounted.current = false;
    };
  }, []);

  const fetchData = async () => {
    if (dataLoaded.current && shopDataLoaded.current) return;
    
    try {
      // Fetch user data
      if (!dataLoaded.current) {
        const userData = await api.fetchWithAuth('/user/profile/');
        if (!isMounted.current) return;
        
        if (userData) {
          setUserData({
            first_name: userData.first_name || '',
            last_name: userData.last_name || '',
            email: userData.email || ''
          });
        }
        dataLoaded.current = true;
      }
      
      // Fetch shop data
      if (!shopDataLoaded.current) {
        const shopData = await api.fetchWithAuth('/shop/profile/');
        if (!isMounted.current) return;
        
        if (shopData) {
          setShopData({
            name: shopData.name || '',
            phone: shopData.phone || '',
            email: shopData.email || '',
            address: shopData.address || '',
            city: shopData.city || '',
            state: shopData.state || '',
            zip_code: shopData.zip_code || ''
          });
        }
        shopDataLoaded.current = true;
      }

      // Fetch default deposit amount
      try {
        const depositResponse = await api.fetchWithAuth('/settings/default-deposit/');
        if (!isMounted.current) return;
        if (depositResponse && depositResponse.default_deposit_amount) {
          setDepositSettings({
            default_deposit_amount: depositResponse.default_deposit_amount
          });
        }
      } catch (err) {
        // Non-critical â€” deposit settings may not exist yet
        console.log('No deposit settings found, using defaults');
      }
      
      setError(null);
      setShopError(null);
    } catch (error) {
      if (!isMounted.current) return;
      console.error('Failed to load data:', error);
      setError('Failed to load data. Please try again.');
    } finally {
      if (isMounted.current) {
        setLoading(false);
      }
    }
  };

  const handleUserChange = (event) => {
    const { name, value } = event.target;
    setUserData(prevData => ({
      ...prevData,
      [name]: value
    }));
  };

  const handleShopChange = (event) => {
    const { name, value } = event.target;
    setShopData(prevData => ({
      ...prevData,
      [name]: value
    }));
  };

  const handleUserSave = async () => {
    setSaving(true);
    setSuccess(false);
    setError(null);

    try {
      await api.fetchWithAuth('/user/profile/', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(userData)
      });
      
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (error) {
      console.error('Failed to update user settings:', error);
      setError('Failed to update user settings. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleShopSave = async () => {
    setShopSaving(true);
    setShopSuccess(false);
    setShopError(null);

    try {
      await api.fetchWithAuth('/shop/profile/', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(shopData)
      });
      
      setShopSuccess(true);
      setTimeout(() => setShopSuccess(false), 3000);
    } catch (error) {
      console.error('Failed to update shop settings:', error);
      setShopError('Failed to update shop settings. Please try again.');
    } finally {
      setShopSaving(false);
    }
  };

  const handleSaveDeposit = async () => {
    setDepositSaving(true);
    setDepositError(null);
    setDepositSuccess(false);

    try {
      await api.fetchWithAuth('/settings/default-deposit/', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          default_deposit_amount: depositSettings.default_deposit_amount || null
        }),
      });

      setDepositSuccess(true);
      setTimeout(() => setDepositSuccess(false), 3000);
    } catch (err) {
      console.error('Failed to save deposit settings:', err);
      setDepositError('Failed to save deposit settings. Please try again.');
    } finally {
      setDepositSaving(false);
    }
  };

  const renderEmailServicePanel = () => {
    if (hasAftercareAccess) {
      // Show active email service for standard/growth studio users
      return (
        <Grid item xs={12}>
          <Paper elevation={2} sx={{ p: 3, backgroundColor: '#f8f9fa' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <EmailIcon sx={{ mr: 1, color: 'primary.main' }} />
              <Typography variant="h6" gutterBottom sx={{ mb: 0 }}>
                Email Service Information
              </Typography>
              <Chip 
                label={currentTier.toUpperCase()} 
                color="primary" 
                size="small" 
                sx={{ ml: 2 }}
              />
            </Box>
            
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={8}>
                <Typography variant="body2" color="text.secondary">
                  Aftercare emails are automatically sent from <strong>noreply@inkedupmanager.com</strong> using our professional email service. 
                  Clients can reply directly to your shop email address above.
                </Typography>
              </Grid>
              <Grid item xs={12} md={4} sx={{ textAlign: { xs: 'left', md: 'right' } }}>
                <Chip 
                  label="âœ“ Email Service Active" 
                  color="success" 
                  variant="outlined"
                  sx={{ mb: 1 }}
                />
                <br />
                <Chip 
                  label="âœ“ Professional Delivery" 
                  color="primary" 
                  variant="outlined"
                />
              </Grid>
            </Grid>

            <Alert severity="info" sx={{ mt: 2 }}>
              <Typography variant="body2">
                <strong>No email setup required!</strong> We handle all email delivery, authentication, and compliance. 
                Just make sure your shop information above is accurate - it will appear in all client communications.
              </Typography>
            </Alert>
          </Paper>
        </Grid>
      );
    } else {
      // Show upgrade prompt for basic/unpaid users
      return (
        <Grid item xs={12}>
          <Paper elevation={2} sx={{ p: 3, backgroundColor: '#fafafa', border: '1px dashed #ccc' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <LockIcon sx={{ mr: 1, color: 'text.secondary' }} />
              <Typography variant="h6" gutterBottom sx={{ mb: 0 }}>
                Automated Email Service
              </Typography>
              <Chip 
                label="STANDARD/GROWTH STUDIO FEATURE" 
                color="warning" 
                size="small" 
                sx={{ ml: 2 }}
              />
            </Box>
            
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Send professional aftercare instruction emails automatically to your clients after their tattoo sessions.
            </Typography>

            <Alert severity="warning" sx={{ mb: 2 }}>
              <Typography variant="body2">
                <strong>Email service is not available on the {currentTier} plan.</strong> Upgrade to Standard or Growth Studio to automatically send 
                branded aftercare emails to your clients from noreply@inkedupmanager.com.
              </Typography>
            </Alert>

            <Box sx={{ textAlign: 'center' }}>
              <Button 
                variant="contained" 
                color="primary"
                onClick={() => window.location.href = '/subscription'}
                startIcon={<EmailIcon />}
              >
                Upgrade to Enable Email Service
              </Button>
            </Box>
          </Paper>
        </Grid>
      );
    }
  };

  if (loading) {
    return (
      <Container maxWidth="md">
        <Box sx={{ mt: 4, textAlign: 'center' }}>
          <CircularProgress />
          <Typography sx={{ mt: 2 }}>Loading settings...</Typography>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="md">
      <Box sx={{ mt: 4 }}>
        <Typography variant="h4" gutterBottom align="center">
          Account & Shop Settings
        </Typography>
        
        <Grid container spacing={3}>
          {/* Appearance Settings */}
          <Grid item xs={12}>
            <Paper elevation={2} sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <DarkModeIcon sx={{ mr: 1, color: 'primary.main' }} />
                <Typography variant="h6" sx={{ mb: 0 }}>
                  Appearance
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 2 }}>
                <Box>
                  <Typography variant="body1">
                    Dark Mode
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Switch between light and dark theme
                  </Typography>
                </Box>
                <Switch
                  checked={darkMode}
                  onChange={toggleDarkMode}
                />
              </Box>
            </Paper>
          </Grid>

          {/* User Account Settings */}
          <Grid item xs={12} md={6}>
            <Paper elevation={2} sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                Personal Information
              </Typography>
              
              <Box component="form" sx={{ mt: 2 }}>
                <TextField
                  fullWidth
                  label="First Name"
                  name="first_name"
                  value={userData.first_name}
                  onChange={handleUserChange}
                  margin="normal"
                />
                <TextField
                  fullWidth
                  label="Last Name"
                  name="last_name"
                  value={userData.last_name}
                  onChange={handleUserChange}
                  margin="normal"
                />
                <TextField
                  fullWidth
                  label="Email"
                  name="email"
                  value={userData.email}
                  onChange={handleUserChange}
                  margin="normal"
                />

                <Button
                  variant="contained"
                  color="primary"
                  fullWidth
                  onClick={handleUserSave}
                  disabled={saving}
                  sx={{ mt: 2 }}
                >
                  {saving ? <CircularProgress size={24} /> : 'Save Personal Info'}
                </Button>

                {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
                {success && <Alert severity="success" sx={{ mt: 2 }}>Personal information updated successfully!</Alert>}
              </Box>
            </Paper>
          </Grid>

          {/* Shop Settings */}
          <Grid item xs={12} md={6}>
            <Paper elevation={2} sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                Shop Information
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                This information will be used in{hasAftercareAccess ? ' aftercare emails and' : ''} official communications.
              </Typography>
              
              <Box component="form" sx={{ mt: 2 }}>
                <TextField
                  fullWidth
                  label="Shop Name"
                  name="name"
                  value={shopData.name}
                  onChange={handleShopChange}
                  margin="normal"
                  required
                  helperText="This will appear in all client communications"
                />
                <TextField
                  fullWidth
                  label="Phone Number"
                  name="phone"
                  value={shopData.phone}
                  onChange={handleShopChange}
                  margin="normal"
                  placeholder="(555) 123-4567"
                  helperText="Clients will use this to contact you"
                />
                <TextField
                  fullWidth
                  label="Shop Email"
                  name="email"
                  value={shopData.email}
                  onChange={handleShopChange}
                  margin="normal"
                  type="email"
                  helperText={hasAftercareAccess ? "Clients can reply to aftercare emails at this address" : "Primary contact email for your shop"}
                />
                <TextField
                  fullWidth
                  label="Address"
                  name="address"
                  value={shopData.address}
                  onChange={handleShopChange}
                  margin="normal"
                  placeholder="123 Main Street"
                />
                
                <Grid container spacing={2} sx={{ mt: 1 }}>
                  <Grid item xs={6}>
                    <TextField
                      fullWidth
                      label="City"
                      name="city"
                      value={shopData.city}
                      onChange={handleShopChange}
                    />
                  </Grid>
                  <Grid item xs={3}>
                    <TextField
                      fullWidth
                      label="State"
                      name="state"
                      value={shopData.state}
                      onChange={handleShopChange}
                      placeholder="GA"
                    />
                  </Grid>
                  <Grid item xs={3}>
                    <TextField
                      fullWidth
                      label="Zip Code"
                      name="zip_code"
                      value={shopData.zip_code}
                      onChange={handleShopChange}
                      placeholder="30309"
                    />
                  </Grid>
                </Grid>

                <Button
                  variant="contained"
                  color="primary"
                  fullWidth
                  onClick={handleShopSave}
                  disabled={shopSaving}
                  sx={{ mt: 3 }}
                >
                  {shopSaving ? <CircularProgress size={24} /> : 'Save Shop Info'}
                </Button>

                {shopError && <Alert severity="error" sx={{ mt: 2 }}>{shopError}</Alert>}
                {shopSuccess && <Alert severity="success" sx={{ mt: 2 }}>Shop information updated successfully!</Alert>}
              </Box>
            </Paper>
          </Grid>

          {/* Email Service Panel - Conditional based on subscription */}
          {renderEmailServicePanel()}

          {/* Deposit Settings - Available to all tiers */}
          <Grid item xs={12}>
              <Paper elevation={2} sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <AttachMoneyIcon sx={{ mr: 1, color: 'primary.main' }} />
                  <Typography variant="h6" sx={{ mb: 0 }}>
                    Deposit Settings
                  </Typography>
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Set a default deposit amount that will be pre-filled when requesting deposits from clients.
                  You can always override this per appointment.
                </Typography>

                {depositError && (
                  <Alert severity="error" sx={{ mb: 2 }} onClose={() => setDepositError(null)}>
                    {depositError}
                  </Alert>
                )}
                {depositSuccess && (
                  <Alert severity="success" sx={{ mb: 2 }}>
                    Deposit settings saved!
                  </Alert>
                )}

                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Default Deposit Amount"
                      type="number"
                      value={depositSettings.default_deposit_amount}
                      onChange={(e) => setDepositSettings({
                        ...depositSettings,
                        default_deposit_amount: e.target.value
                      })}
                      fullWidth
                      InputProps={{
                        startAdornment: <AttachMoneyIcon sx={{ color: 'text.secondary', mr: 0.5 }} />,
                        inputProps: { min: 0, step: 5 }
                      }}
                      helperText="Leave empty if you prefer to set it per appointment"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Button
                      variant="contained"
                      onClick={handleSaveDeposit}
                      disabled={depositSaving}
                      startIcon={depositSaving ? <CircularProgress size={16} /> : <AttachMoneyIcon />}
                    >
                      {depositSaving ? 'Saving...' : 'Save Deposit Settings'}
                    </Button>
                  </Grid>
                </Grid>

                <Alert severity="info" sx={{ mt: 2 }}>
                  <Typography variant="body2">
                    Deposits are processed through Stripe Connect. Make sure your Stripe account is connected 
                    before requesting deposits. Funds go directly to your Stripe account with zero platform fees.
                  </Typography>
                </Alert>
              </Paper>
            </Grid>
        </Grid>
      </Box>
    </Container>
  );
};

export default SettingsPage;